import { isConversationTurnStart } from '../../../shared/conversationTurns';
import { useMemo, useState } from 'react';
import type { CoworkMessage } from '../../types/cowork';
import { stripThinkingLeak } from '../../utils/thinkingLeak';
import { i18nService } from '../../services/i18n';

/** 当前会话已加载完整消息；输入为空列出提问锚点，输入后搜索公开正文及工具结果。 */
export default function ConversationSearch({ messages, onLocate, onClose }: {
  messages: CoworkMessage[];
  onLocate: (turnId: string, messageId: string) => void;
  onClose: () => void;
}) {
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(0);
  const matches = useMemo(() => {
    let turnId = '';
    const needle = query.trim().toLocaleLowerCase();
    return messages.flatMap(message => {
      if (isConversationTurnStart(message)) turnId = message.id;
      if (!turnId || message.metadata?.isThinking) return [];
      const content = message.type === 'assistant' ? stripThinkingLeak(message.content) : message.content;
      if (!needle && message.type !== 'user') return [];
      const index = needle ? content.toLocaleLowerCase().indexOf(needle) : 0;
      if (index < 0) return [];
      const start = Math.max(0, index - 50);
      return [{ id: message.id, turnId, excerpt: content.slice(start, start + 240), index: index - start, size: needle.length }];
    });
  }, [messages, query]);
  /** 上下切换命中项并定位所属问答轮次；列表全文保留可读片段。 */
  const move = (offset: number) => {
    if (!matches.length) return;
    const next = (selected + offset + matches.length) % matches.length;
    setSelected(next); onLocate(matches[next].turnId, matches[next].id);
  };
  return <section className="shrink-0 border-b border-claude-border dark:border-claude-darkBorder p-3" aria-label={i18nService.t('conversationSearch')}>
    <div className="flex items-center gap-2">
      <input autoFocus value={query} onChange={event => { setQuery(event.target.value); setSelected(0); }}
        onKeyDown={event => { if (event.key === 'Escape') onClose(); if (event.key === 'Enter') { event.preventDefault(); move(event.shiftKey ? -1 : 1); } }}
        placeholder={i18nService.t('conversationSearchHint')} aria-label={i18nService.t('conversationSearch')}
        className="min-w-0 flex-1 rounded-lg px-3 py-1.5 border border-claude-border dark:border-claude-darkBorder bg-transparent text-sm" />
      <span className="text-xs">{matches.length ? Math.min(selected + 1, matches.length) : 0}/{matches.length}</span>
      <button type="button" onClick={() => move(-1)} aria-label={i18nService.t('conversationPrevious')}>↑</button>
      <button type="button" onClick={() => move(1)} aria-label={i18nService.t('conversationNext')}>↓</button>
      <button type="button" onClick={onClose} aria-label={i18nService.t('close')}>×</button>
    </div>
    <div className="max-h-40 overflow-y-auto mt-2">{matches.map((match, index) => <button key={match.id} type="button"
      onClick={() => { setSelected(index); onLocate(match.turnId, match.id); }}
      className={`block w-full px-2 py-1 text-left text-xs break-words rounded ${selected === index ? 'bg-claude-accent/10' : ''}`}>
      {match.excerpt.slice(0, match.index)}<mark>{match.excerpt.slice(match.index, match.index + match.size)}</mark>{match.excerpt.slice(match.index + match.size)}
    </button>)}</div>
  </section>;
}
